<template>
  <div class="viq-gap-q">
    <p class="viq-gap-q__intro">
      Answer the following questions to help VIQ identify compliance gaps. Select the most appropriate option for each.
    </p>

    <div class="viq-gap-q__list">
      <div
        v-for="(q, i) in questions"
        :key="i"
        class="viq-gap-q__card"
      >
        <div class="viq-gap-q__card-num">{{ i + 1 }}</div>
        <div class="viq-gap-q__card-content">
          <h4 class="viq-gap-q__card-question">{{ q.text || q.question || q }}</h4>
          <div v-if="q.options && q.options.length > 0" class="viq-gap-q__options">
            <label
              v-for="(opt, j) in q.options"
              :key="j"
              class="viq-gap-q__option"
              :class="{ 'viq-gap-q__option--selected': answers[i] === (opt.value || opt) }"
            >
              <input
                type="radio"
                :name="'q-' + i"
                :value="opt.value || opt"
                class="viq-gap-q__option-radio"
                @change="setAnswer(i, opt.value || opt)"
              />
              <span class="viq-gap-q__option-indicator" />
              <span class="viq-gap-q__option-text">{{ opt.label || opt }}</span>
            </label>
          </div>
          <el-input
            v-else
            v-model="answers[i]"
            type="textarea"
            :rows="2"
            size="small"
            placeholder="Your answer..."
          />
        </div>
      </div>
    </div>

    <div v-if="isStreaming" class="viq-gap-q__loading">
      <span class="viq-gap-q__spinner" />
      Loading questions...
    </div>

    <div v-if="!isStreaming" class="viq-gap-q__actions">
      <el-button
        type="primary"
        :disabled="!allAnswered"
        @click="submit"
      >
        Analyse Gaps
      </el-button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ViqGapQuestions",
  props: {
    questions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      answers: {},
    };
  },
  computed: {
    ...mapState("Viq", ["isStreaming"]),
    allAnswered() {
      return this.questions.length > 0 &&
        this.questions.every((_, i) => this.answers[i] !== undefined && this.answers[i] !== "");
    },
  },
  watch: {
    questions: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          const newAnswers = {};
          val.forEach((_, i) => {
            newAnswers[i] = this.answers[i] || "";
          });
          this.answers = newAnswers;
        }
      },
    },
  },
  methods: {
    setAnswer(index, value) {
      this.$set(this.answers, index, value);
    },
    submit() {
      const result = this.questions.map((q, i) => ({
        question: q.text || q.question || q,
        answer: this.answers[i],
      }));
      this.$emit("submit", result);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-gap-q {
  &__intro {
    font-size: $viq-font-base;
    color: $viq-text-secondary;
    margin: 0 0 $viq-space-lg;
    line-height: 1.5;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: $viq-space-md;
  }

  &__card {
    display: flex;
    gap: $viq-space-md;
    padding: $viq-space-lg;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;

    &-num {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: $viq-bg-secondary;
      border-radius: 50%;
      font-size: $viq-font-sm;
      font-weight: 700;
      color: $viq-text-secondary;
    }

    &-content {
      flex: 1;
    }

    &-question {
      font-size: $viq-font-base;
      font-weight: 600;
      color: $viq-text-primary;
      margin: 0 0 $viq-space-md;
      line-height: 1.4;
    }
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: $viq-space-sm;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    padding: $viq-space-sm $viq-space-md;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;
    cursor: pointer;
    transition: border-color $viq-transition-fast, background $viq-transition-fast;

    &:hover {
      border-color: $viq-accent;
    }

    &--selected {
      border-color: $viq-accent;
      background: $viq-citation-bg;
    }

    &-radio {
      display: none;
    }

    &-indicator {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      border: 2px solid $viq-border;
      border-radius: 50%;
      transition: border-color $viq-transition-fast;
      position: relative;

      .viq-gap-q__option--selected & {
        border-color: $viq-accent;

        &::after {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 6px;
          height: 6px;
          background: $viq-accent;
          border-radius: 50%;
        }
      }
    }

    &-text {
      font-size: $viq-font-sm;
      color: $viq-text-primary;
    }
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    padding: $viq-space-lg;
    justify-content: center;
    color: $viq-text-secondary;
    font-size: $viq-font-sm;
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid $viq-border;
    border-top-color: $viq-accent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  &__actions {
    margin-top: $viq-space-lg;
    display: flex;
    justify-content: flex-end;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
